export type OrgFilterExtraInfoField = {
  dbFieldPath: string;
  icon?: string;
  tooltip?: string;
  /** Display order — ascending. Also the input to the display-count limits (filter UI vs. everywhere else). */
  index?: number;
};

/** Filter dropdowns/checkbox lists show only the first 2 attributes by index. */
export const ORG_EXTRA_INFO_FIELDS_LIMIT_FILTER_UI = 2;
/** Every other surface (C360, evidence company pickers, Explore Agent, etc.) shows the first 4. */
export const ORG_EXTRA_INFO_FIELDS_LIMIT_DEFAULT = 4;

function sortAndLimit(
  fields: OrgFilterExtraInfoField[],
  limit?: number
): OrgFilterExtraInfoField[] {
  const sorted = [...fields].sort((a, b) => (a.index ?? Infinity) - (b.index ?? Infinity));
  return typeof limit === 'number' ? sorted.slice(0, limit) : sorted;
}

export type OrgFilterConfig = {
  type?: string;
  dbFieldPath?: string | null;
  extraUI?: {
    extraInfoFields?: OrgFilterExtraInfoField[];
  };
  childFilters?: Record<string, OrgFilterConfig>;
};

export const ORG_FILTER_DB_FIELD_PATHS = new Set([
  'connectedOrgs._id',
  'connectedOrgs.orgId',
  'company._id',
  'relatedRequests.orgId',
]);

export function isOrgFetchableCheckboxFilter(filter?: OrgFilterConfig | null): boolean {
  if (!filter) {
    return false;
  }

  return (
    filter.type === 'fetchableCheckbox' &&
    ORG_FILTER_DB_FIELD_PATHS.has(filter.dbFieldPath ?? '')
  );
}

export function findOrgFetchableCheckboxFilter(
  filtersConfig: Record<string, OrgFilterConfig> | OrgFilterConfig[] | undefined
): OrgFilterConfig | undefined {
  if (!filtersConfig) {
    return undefined;
  }

  const filterList = Array.isArray(filtersConfig)
    ? filtersConfig
    : Object.values(filtersConfig);

  for (const filter of filterList) {
    if (isOrgFetchableCheckboxFilter(filter)) {
      return filter;
    }
  }

  return undefined;
}

/**
 * @param limit Caps the returned (index-sorted) list — pass
 * `ORG_EXTRA_INFO_FIELDS_LIMIT_FILTER_UI` in filter dropdowns/checkbox lists,
 * `ORG_EXTRA_INFO_FIELDS_LIMIT_DEFAULT` everywhere else. Omit to get the full
 * sorted list (e.g. for building the "manage fields" config UI itself).
 */
export function getOrgExtraInfoFieldsFromFiltersConfig(
  filtersConfig: Record<string, OrgFilterConfig> | undefined,
  limit?: number
): OrgFilterExtraInfoField[] {
  const orgFilter = findOrgFetchableCheckboxFilter(filtersConfig);
  const configuredFields = orgFilter?.extraUI?.extraInfoFields;
  if (!configuredFields?.length) {
    return [];
  }
  return sortAndLimit(configuredFields, limit);
}

/** @param limit See {@link getOrgExtraInfoFieldsFromFiltersConfig}. */
export function getOrgExtraInfoFieldsForFilter(
  filter?: OrgFilterConfig | null,
  ideasFiltersConfig?: {
    idea?: Record<string, OrgFilterConfig>;
    evidence?: Record<string, OrgFilterConfig>;
  },
  limit?: number
): OrgFilterExtraInfoField[] {
  if (!isOrgFetchableCheckboxFilter(filter)) {
    return [];
  }

  const configuredFields = filter?.extraUI?.extraInfoFields;
  if (configuredFields?.length) {
    return sortAndLimit(configuredFields, limit);
  }

  if (ideasFiltersConfig) {
    for (const bucket of [ideasFiltersConfig.evidence, ideasFiltersConfig.idea]) {
      const orgFilter = findOrgFetchableCheckboxFilter(bucket);
      const bucketFields = orgFilter?.extraUI?.extraInfoFields;
      if (bucketFields?.length) {
        return sortAndLimit(bucketFields, limit);
      }
    }
  }

  return [];
}
